import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { type ReactNode, useState } from 'react';

import Slider from 'react-slick';

import isInteger from 'lodash/fp/isInteger';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import type { StaticImageData } from 'Components/Image';
import { Typography } from 'Components/Typography';

import { dreamTeamContent } from 'assets/content/landingPages/stylingGel';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const CardContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 138px 86px 230px;
  border: 1px solid;
  width: 100%;
  background: ${theme.colors.neutral[200]};
  ${legacyTheme.breakpoints.up('lg')} {
    grid-template-rows: auto auto auto;
  }
`;

const CardSectionNameWrapper = styled.div`
  padding: 18px;
  border-right: 1px solid;
`;

const StyledNameText = styled(Typography)`
  font-family: ${legacyTheme.typography.simplonMono};
  text-transform: uppercase;
  font-size: 16px;
  font-weight: 500;
`;

const StyledCaptionText = styled(Typography)`
  font-family: ${legacyTheme.typography.simplonMono};
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 500;
  ${legacyTheme.breakpoints.up('sm')} {
    font-size: 12px;
  }
  ${legacyTheme.breakpoints.up('lg')} {
    font-size: 14px;
  }
`;

const CardSectionCaptionWrapper = styled.div`
  display: flex;
  align-items: center;
  border-top: 1px solid;
  border-bottom: 1px solid;
  grid-column: 1 / -1;
  padding: 18px;
  height: 86px;
`;

const CardSectionBodyWrapper = styled.div`
  padding: 18px;
  grid-column: 1 / -1;
  ${legacyTheme.breakpoints.up('sm')} {
    height: 185px;
  }
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CardHeaderContainer = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr 1fr;
  grid-column: 1 / -1;
  overflow: hidden;
`;

type CardDreamTeamProps = {
  title: ReactNode;
  caption: string;
  image: StaticImageData;
  bodyText: string;
  alt: string;
};

const CardDreamTeam = ({ alt, title, caption, image, bodyText }: CardDreamTeamProps) => (
  <CardContainer>
    <CardHeaderContainer>
      <CardSectionNameWrapper>
        <StyledNameText>{title}</StyledNameText>
      </CardSectionNameWrapper>
      <Img alt={alt} src={image.src} />
    </CardHeaderContainer>
    <CardSectionCaptionWrapper>
      <StyledCaptionText>{caption}</StyledCaptionText>
    </CardSectionCaptionWrapper>
    <CardSectionBodyWrapper>
      <Typography>{bodyText}</Typography>
    </CardSectionBodyWrapper>
  </CardContainer>
);

const SectionContainer = styled('div')`
  background: black;
  width: 100%;
`;
const HeaderSectionContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
  padding: 42px 24px;
  ${legacyTheme.breakpoints.up('sm')} {
    flex-direction: row;
    align-items: flex-start;
    padding: 70px;
    text-align: left;
  }
  ${legacyTheme.breakpoints.up('lg')} {
    flex-direction: row;
    padding: 85px 110px;
    text-align: left;
  }
`;

const StyledTextTitle = styled(Typography)`
  font-family: ${legacyTheme.typography.simplonMono};
  text-transform: uppercase;
  font-size: 28px;
  margin-bottom: 32px;
  width: 100%;
  line-height: 1;
  ${legacyTheme.breakpoints.up('sm')} {
    width: 50%;
    margin-bottom: 0;
    font-size: 36px;
  }
  ${legacyTheme.breakpoints.up('lg')} {
    width: 50%;
  }
`;

const StyledTextBodyText = styled(Typography)`
  width: 100%;
  ${legacyTheme.breakpoints.up('sm')} {
    width: 50%;
    margin-left: 18px;
  }
  ${legacyTheme.breakpoints.up('lg')} {
    width: 50%;
    padding: 0 100px 0 18px;
  }
`;

const CardsContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-gap: 18px;
  padding: 0 32px;
  ${legacyTheme.breakpoints.up('sm')} {
    padding: 0 70px;
  }
  ${legacyTheme.breakpoints.up('lg')} {
    grid-gap: 56px;
    padding: 0 110px;
  }
`;

const StyledSlider = styled(Slider)`
  width: 100%;

  .slick-dots {
    bottom: 12px;

    li {
      margin: 0;
    }

    li button {
      padding: 0;
    }

    li button::before {
      color: ${theme.colors.neutral[200]};
      opacity: 0.25;
    }

    li.slick-active button::before {
      color: white;
      opacity: 1;
    }
  }

  .slick-track .slick-slide:last-child {
    padding-right: 18px;
  }
`;

const CardDreamTeamContainer = styled('div')`
  display: flex;
  padding: 0 0 72px 18px;
  ${legacyTheme.breakpoints.up('sm')} {
    padding: 0 0 72px 18px;
  }
  ${legacyTheme.breakpoints.up('lg')} {
    padding: 0 0 72px;
  }
`;

const Dot = styled('div')<{ currentSlide: number; index: number }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(props) =>
    props.currentSlide === props.index ? theme.colors.neutral[100] : theme.colors.neutral[200]};
  opacity: ${(props) => (props.currentSlide === props.index ? '1' : '0.25')};
`;

export const DreamTeam = () => {
  const { sectionTitle, SectionbodyText, cardsData } = dreamTeamContent;
  const [currentSlide, setCurrentSlide] = useState(0);

  const { isMobile, isDesktop } = useResponsiveVariant();

  const cards = cardsData.map(({ alt, title, caption, bodyText, image }) => (
    <CardDreamTeamContainer key={caption}>
      <CardDreamTeam alt={alt} bodyText={bodyText} caption={caption} image={image} title={title} />
    </CardDreamTeamContainer>
  ));
  const customPagingDot = (i: number) => <Dot currentSlide={currentSlide} index={i} />;

  return (
    <SectionContainer>
      <HeaderSectionContainer>
        <StyledTextTitle>{sectionTitle}</StyledTextTitle>

        <StyledTextBodyText>{SectionbodyText}</StyledTextBodyText>
      </HeaderSectionContainer>
      {isDesktop ? (
        <CardsContainer>{cards}</CardsContainer>
      ) : (
        <StyledSlider
          afterChange={(slide) => (isInteger(slide) ? setCurrentSlide(slide) : setCurrentSlide(3))}
          arrows={false}
          customPaging={customPagingDot}
          dots={isMobile}
          focusOnSelect
          infinite={false}
          slidesToShow={isMobile ? 1.2 : 2.5}
        >
          {cards}
        </StyledSlider>
      )}
    </SectionContainer>
  );
};
